import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Security as SecurityIcon,
  QrCode2 as QrCodeIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import mfaService from '../services/mfaService';
import { useSnackbar } from '../contexts/SnackbarContext';

interface MFASetupProps {
  onMFAStatusChange?: (enabled: boolean) => void;
}

const MFASetup: React.FC<MFASetupProps> = ({ onMFAStatusChange }) => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  
  // Setup state
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationToken, setVerificationToken] = useState('');
  const [setupStep, setSetupStep] = useState<'qr' | 'verify' | 'backup'>('qr');
  
  // Disable state
  const [disablePassword, setDisablePassword] = useState('');
  
  const [error, setError] = useState('');
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    checkMFAStatus();
  }, []);

  const checkMFAStatus = async () => {
    try {
      setLoading(true);
      const enabled = await mfaService.getMFAStatus();
      setMfaEnabled(enabled);
      onMFAStatusChange?.(enabled);
    } catch (error) {
      console.error('Error checking MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupMFA = async () => {
    try {
      setError('');
      setLoading(true);
      const data = await mfaService.setupMFA();
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setBackupCodes(data.backupCodes);
      setSetupStep('qr');
      setSetupDialogOpen(true);
    } catch (error: any) {
      setError(error.message);
      showSnackbar(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMFA = async () => {
    if (!verificationToken || verificationToken.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setError('');
      setLoading(true);
      const message = await mfaService.verifyAndEnableMFA(verificationToken);
      setSetupStep('backup');
      showSnackbar(message, 'success');
    } catch (error: any) {
      setError(error.message);
      showSnackbar(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSetup = () => {
    setSetupDialogOpen(false);
    setMfaEnabled(true);
    onMFAStatusChange?.(true);
    setVerificationToken('');
    setSetupStep('qr');
    showSnackbar('MFA has been successfully enabled!', 'success');
  };

  const handleDisableMFA = async () => {
    if (!disablePassword) {
      setError('Please enter your password');
      return;
    }

    try {
      setError('');
      setLoading(true);
      const message = await mfaService.disableMFA(disablePassword);
      setDisableDialogOpen(false);
      setMfaEnabled(false);
      onMFAStatusChange?.(false);
      setDisablePassword('');
      showSnackbar(message, 'success');
    } catch (error: any) {
      setError(error.message);
      showSnackbar(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar(`${label} copied to clipboard!`, 'success');
  };

  if (loading && !setupDialogOpen && !disableDialogOpen) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        border: '1px solid #e0e0e0',
        borderLeft: '5px solid #003366'
      }}
      aria-labelledby="mfa-heading"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1, color: '#003366' }} />
          <Typography variant="h6" component="h2" sx={{ color: '#003366', fontWeight: 600 }} id="mfa-heading">
            Multi-Factor Authentication (MFA)
          </Typography>
        </Box>
        <Chip
          icon={mfaEnabled ? <CheckIcon /> : <CancelIcon />}
          label={mfaEnabled ? 'Enabled' : 'Disabled'}
          color={mfaEnabled ? 'success' : 'default'}
          size="small"
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add an extra layer of security to your account with multi-factor authentication.
        You'll need to enter a code from your authenticator app when you log in.
      </Typography>

      {mfaEnabled ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>MFA is active</strong> - Your account is protected with two-factor authentication.
            </Typography>
          </Alert>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDisableDialogOpen(true)}
            startIcon={<CancelIcon />}
            fullWidth
          >
            Disable MFA
          </Button>
        </Box>
      ) : (
        <Box>
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>MFA is not enabled</strong> - Your account is less secure without two-factor authentication.
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={handleSetupMFA}
            startIcon={<SecurityIcon />}
            fullWidth
            sx={{ bgcolor: '#003366', '&:hover': { bgcolor: '#002244' } }}
          >
            Enable MFA
          </Button>
        </Box>
      )}

      {/* Setup Dialog */}
      <Dialog
        open={setupDialogOpen}
        onClose={() => setupStep === 'backup' ? handleCompleteSetup() : setSetupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#003366', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QrCodeIcon sx={{ mr: 1 }} />
            Setup Multi-Factor Authentication
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {setupStep === 'qr' && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Step 1:</strong> Scan the QR code with your authenticator app
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use Google Authenticator, Authy, or any TOTP-compatible app
              </Typography>
              
              {qrCode && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <img src={qrCode} alt="MFA QR Code" style={{ maxWidth: '250px' }} />
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Or enter this code manually:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  value={secret}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                  sx={{ fontFamily: 'monospace' }}
                />
                <Tooltip title="Copy secret">
                  <IconButton onClick={() => copyToClipboard(secret, 'Secret')}>
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {setupStep === 'verify' && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationToken}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationToken(value);
                  setError('');
                }}
                placeholder="123456"
                inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                error={!!error}
                helperText={error}
                autoFocus
              />
            </Box>
          )}

          {setupStep === 'backup' && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>MFA Enabled!</strong> Save your backup codes in a safe place.
                </Typography>
              </Alert>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Backup Codes</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use these codes if you lose access to your authenticator app. Each code can only be used once.
              </Typography>
              
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 2 }}>
                <Grid container spacing={1}>
                  {backupCodes.map((code, index) => (
                    <Grid item xs={6} key={index}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                        {code}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => copyToClipboard(backupCodes.join('\n'), 'Backup codes')}
                fullWidth
                sx={{ mb: 1 }}
              >
                Copy All Codes
              </Button>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  ⚠️ Store these codes securely. You won't be able to see them again!
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {setupStep === 'qr' && (
            <>
              <Button onClick={() => setSetupDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => setSetupStep('verify')}
                sx={{ bgcolor: '#003366' }}
              >
                Next
              </Button>
            </>
          )}
          {setupStep === 'verify' && (
            <>
              <Button onClick={() => setSetupStep('qr')}>Back</Button>
              <Button
                variant="contained"
                onClick={handleVerifyMFA}
                disabled={verificationToken.length !== 6 || loading}
                sx={{ bgcolor: '#003366' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify & Enable'}
              </Button>
            </>
          )}
          {setupStep === 'backup' && (
            <Button
              variant="contained"
              onClick={handleCompleteSetup}
              sx={{ bgcolor: '#003366' }}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#B31B1B', color: 'white' }}>
          Disable Multi-Factor Authentication
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Disabling MFA will make your account less secure. Are you sure?
            </Typography>
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your password to confirm:
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={disablePassword}
            onChange={(e) => {
              setDisablePassword(e.target.value);
              setError('');
            }}
            error={!!error}
            helperText={error}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDisableDialogOpen(false);
            setDisablePassword('');
            setError('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisableMFA}
            disabled={!disablePassword || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Disable MFA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MFASetup;
